import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { UiIconActionComponent } from '../../atoms/icon-action/icon-action.component';
import { UiTagColor, UiTagComponent } from '../../atoms/tag/tag.component';
import { UiCompactListColumn, UiCompactListRowActions } from './compact-list.types';

/**
 * Molécula: lista compacta de registros relacionados dentro de un formulario.
 * Cabecera fija, scroll solo en las filas, acciones Editar y Eliminar o,
 * con `rowActions="view-download"`, Ver y Descargar, o solo Ver con `rowActions="view"`.
 * Presentacional: no pagina, no filtra y no persiste.
 */
@Component({
  selector: 'ui-compact-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiTagComponent, UiIconActionComponent],
  templateUrl: './compact-list.component.html',
  styleUrl: './compact-list.component.scss',
  host: { class: 'ui-compact-list-host' },
})
export class UiCompactListComponent<T = unknown> {
  readonly columns = input.required<readonly UiCompactListColumn<T>[]>();
  readonly rows = input<readonly T[]>([]);
  readonly rowKey = input('id');
  /** Si se indica, cada fila muestra este campo arriba de las celdas. */
  readonly rowTitleKey = input<string | null>(null);
  /** Si se indica, cada fila muestra este campo como tag en una segunda línea. */
  readonly rowTagKey = input<string | null>(null);
  readonly rowTagColorMap = input<Readonly<Record<string, UiTagColor>> | null>(null);
  readonly emptyText = input('No hay registros');
  readonly showHeader = input(true);
  readonly showActions = input(true);
  readonly rowActions = input<UiCompactListRowActions>('edit-delete');

  readonly edit = output<T>();
  readonly delete = output<T>();
  readonly view = output<T>();
  readonly download = output<T>();

  protected readonly gridTemplate = computed(() => {
    const cols = this.columns()
      .map(col => col.width ?? 'minmax(0, 1fr)')
      .join(' ');
    return this.showActions() ? `${cols} auto` : cols;
  });

  protected rowId(row: T): string {
    const key = this.rowKey() as keyof T;
    return String(row?.[key] ?? '');
  }

  protected rowTitle(row: T): string | null {
    const key = this.rowTitleKey();
    if (!key) return null;
    const value = (row as Record<string, unknown>)[key];
    return value == null || value === '' ? null : String(value);
  }

  protected rowTag(row: T): { label: string; color: UiTagColor } | null {
    const key = this.rowTagKey();
    if (!key) return null;
    const value = (row as Record<string, unknown>)[key];
    if (value == null || value === '') return null;
    const label = String(value);
    return { label, color: this.rowTagColorMap()?.[label] ?? 'neutral' };
  }

  protected cellText(col: UiCompactListColumn<T>, row: T): string {
    if (col.value) return col.value(row) || '—';
    const raw = (row as Record<string, unknown>)[col.key];
    if (raw == null || raw === '') return '—';
    return String(raw);
  }

  protected cellTagColor(col: UiCompactListColumn<T>, row: T): UiTagColor {
    const value = this.cellText(col, row);
    if (col.tagColor) return col.tagColor(value, row);
    return col.tagColorMap?.[value] ?? 'neutral';
  }
}
